import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GsBulletin } from '@/lib/types';

interface Props {
  notice: GsBulletin | null;
  onClose: () => void;
}

export default function BulletinDetail({ notice, onClose }: Props) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!notice) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [notice, onClose]);

  if (!notice) return null;

  const goToLink = () => {
    const href = notice.link;
    if (!href) return;
    if (/^https?:\/\//i.test(href)) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    const [path, hash] = href.split('#');
    onClose();
    navigate(path || '/');
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 90);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const contact = notice.contact?.trim();
  const contactIsLink = !!contact && /^(https?:\/\/|mailto:|tel:)/i.test(contact);
  const contactHref = contact
    ? (/^(https?:\/\/|mailto:|tel:)/i.test(contact)
      ? contact
      : (contact.includes('@') ? `mailto:${contact}` : undefined))
    : undefined;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label={notice.title}>
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-background-50 rounded-t-xl sm:rounded-lg border border-background-200 gs-rise">
        <span aria-hidden="true" className="absolute top-3 left-1/2 -translate-x-1/2 w-5 h-5 z-10">
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3.5 h-2 rounded-full bg-foreground-950/20 blur-[2px]"></span>
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-300 via-primary-500 to-primary-800"></span>
          <span className="absolute top-[3px] left-[5px] w-1.5 h-1.5 rounded-full bg-background-50/80"></span>
        </span>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close notice"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-foreground-500 hover:text-foreground-900 hover:bg-background-100 cursor-pointer transition-colors z-10"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="p-6 md:p-8 pt-9">
          <span className="inline-block bg-primary-50 text-primary-700 font-label font-semibold uppercase text-[11px] tracking-[0.16em] px-2.5 py-1 rounded-sm">{notice.tag}</span>
          <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl leading-tight text-foreground-950">{notice.title}</h3>

          {notice.body && <p className="mt-4 text-[15px] leading-relaxed text-foreground-700 whitespace-pre-line">{notice.body}</p>}

          {notice.meta && (
            <p className="mt-5 flex items-center gap-2 font-label uppercase tracking-widest text-[11px] text-foreground-500">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>{notice.meta}
            </p>
          )}

          <div className="mt-6 pt-5 border-t border-background-200">
            <p className="font-label uppercase tracking-[0.2em] text-[10px] text-foreground-500 font-semibold">Posted by</p>
            <p className="mt-1.5 font-heading font-bold text-base text-foreground-900">
              {notice.submitter_name?.trim() || 'A GreenSide neighbor'}
            </p>

            {contact && (
              <div className="mt-3">
                <p className="font-label uppercase tracking-[0.2em] text-[10px] text-foreground-500 font-semibold">Get in touch</p>
                {contactHref ? (
                  <a
                    href={contactHref}
                    target={contactIsLink && /^https?:/i.test(contact) ? '_blank' : undefined}
                    rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-2 text-primary-700 hover:text-primary-600 font-semibold break-all cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className={contactIsLink ? 'ri-link' : (contact.includes('@') ? 'ri-mail-line' : 'ri-phone-line')}></i>
                    </span>
                    {contact}
                  </a>
                ) : (
                  <p className="mt-1.5 text-foreground-700 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center shrink-0"><i className="ri-chat-1-line"></i></span>
                    {contact}
                  </p>
                )}
              </div>
            )}

            {notice.link && (
              <button
                type="button"
                onClick={goToLink}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-sm px-5 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
              >
                Take action
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}